import { cellAtWorld, cellCenter } from '../grid.ts';
import { iatan2, idiv, imax, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt } from '../math/rng.ts';
import type { BattleState, Enemy, Hero, SendaNpc } from '../types.ts';
import type { CliffPoint, CliffRules, CliffState, CliffTrip } from '../galo-types.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';
import { orderMove } from './heroes.ts';
import { stepToward } from './enemies.ts';

/**
 * The cliff of the flank (docs/sendas/explorador.md, chapter 2, «El Acantilado del Flanco»; §4, «Las anclas»; the
 * ground in battle/cliff-layout.ts). One rule for its three fights (`rules.senda.cliff.act`):
 *
 * - the cornice (`edge`): Sela drives the anchor (the act's clock); her hammer rings every `hammer`. Soot crows come out
 *   of their nests at whoever is nearest; shriekers (blind, leather-winged) come out of the deep cracks and fly to the
 *   last sound: the hammer, or the hero's bow. Drawing them off Sela is shooting.
 * - the wall (`wall`): a tap on a crack (the `mark` command, its index) sends the hero along its ledge to a spot in the
 *   rope's reach and shoots: a firm crack sounds deep and holds a rope down to the ledge under it (and the hero goes
 *   down it; a rope stays, both ways); a hollow one sounds high and drops the arrow (a nest by it wakes); a crumbling
 *   one holds the hero hanging from it `hold`, and from there the rope reaches the next face down. When it gives, the
 *   rope swings the hero back to where it shot from, struck. A rope passing by a nest wakes its crows; the crew's
 *   scared flocks come in from their side. Sela goes the same way after the hero.
 * - the night (`night`, the test): the same, and every sound calls: a rope's shot rings at its crack, the bow at the
 *   hero, a hollow stone (the mark `cracks + its index`) rings `stone` at its ledge, once (it cracks); the shriekers out of the deep
 *   cracks go to the last sound (a ringing stone first), and a second rope shot close by brings more of them. Their
 *   mother keeps off the hero and shrieks: a ring growing from her for `warn`; who is inside at the end is stunned
 *   (hanging from a crack, the clock still runs).
 *
 * Nothing strands the hero: a crumbling crack gives but never goes (it holds again), ropes are climbed both ways, and
 * a stun ends. Cost: the trip a tick, the few shriekers and the mother; the rest hunts by the common rule.
 */

const fresh = (r: CliffRules): CliffState => ({
  on: 0,
  hang: -1,
  gives: 0,
  from: 0,
  sounded: r.cracks.map(() => 0),
  ropes: [],
  job: -1,
  shotAt: -1,
  trip: null,
  trail: [],
  sela: null,
  woke: r.nests.map(() => 0),
  sound: { x: 0, y: 0, t: -1 },
  stone: { i: -1, until: 0 },
  rung: r.stones.map(() => 0),
  shriekers: [],
  shots: [],
  bite: [],
  bowCall: 0,
  mother: { id: 0, next: 0, ring: -1, x: 0, y: 0, shrieks: 0, call: 0 },
  next: { crow: r.edge?.crows.first ?? 0, shriek: r.edge?.shriek.first ?? 0, crew: r.crew.first, hammer: r.edge?.hammer ?? 0 },
  cd: 0,
  landed: 0,
  hollows: 0,
  falls: 0,
  crumbs: 0,
  done: -1,
});

const near2 = (a: CliffPoint, x: number, y: number, r: number): boolean => withinRange(a.x, a.y, x, y, r);

/** The ledge a point stands on (−1: none). */
export const cliffLedgeAt = (s: BattleState, r: CliffRules, x: number, y: number): number => {
  const c = cellAtWorld(s, x, y);
  for (let i = 0; i < r.ledges.length; i++) if ((r.ledges[i] as CliffRules['ledges'][number]).cells.includes(c)) return i;
  return -1;
};

/** Where a rope from a crack comes down on its ledge: the ledge's cell nearest under the crack. */
export const cliffFoot = (s: BattleState, r: CliffRules, crack: number): CliffPoint => {
  const c = r.cracks[crack] as CliffRules['cracks'][number];
  const l = r.ledges[c.land];
  if (!l) return { x: c.x, y: c.y };
  let best = { x: c.x, y: c.y };
  let bd = 0x7fffffff;
  for (const id of l.cells) {
    const p = cellCenterOf(s, id);
    const d = Math.abs(p.x - c.x) * 2 + Math.abs(p.y - c.y);
    if (d < bd) {
      bd = d;
      best = p;
    }
  }
  return best;
};

const cellCenterOf = (s: BattleState, id: number): CliffPoint => cellCenter(s.map.radius, id);

/** A ledge's cell nearest a point within `reach` of `at` (null: none on it). */
const spotInReach = (s: BattleState, r: CliffRules, ledge: number, at: CliffPoint, reach: number, from: CliffPoint): CliffPoint | null => {
  const l = r.ledges[ledge];
  if (!l) return null;
  let best: CliffPoint | null = null;
  let bd = 0x7fffffff;
  for (const id of l.cells) {
    const p = cellCenterOf(s, id);
    if (!near2(p, at.x, at.y, reach - 150)) continue;
    const d = Math.abs(p.x - from.x) + Math.abs(p.y - from.y);
    if (d < bd) {
      bd = d;
      best = p;
    }
  }
  return best;
};

/** A ledge's cell next to a point on it (not its own): where Sela comes down by the hero. */
const besideOn = (s: BattleState, r: CliffRules, ledge: number, p: CliffPoint): CliffPoint => {
  const l = r.ledges[ledge];
  if (!l) return { x: p.x, y: p.y };
  const own = cellAtWorld(s, p.x, p.y);
  let best = { x: p.x, y: p.y };
  let bd = 0x7fffffff;
  for (const id of l.cells) {
    if (id === own) continue;
    const c = cellCenterOf(s, id);
    const d = Math.abs(c.x - p.x) + Math.abs(c.y - p.y);
    if (d < bd) {
      bd = d;
      best = c;
    }
  }
  return best;
};

/** The hero's taps: a crack (its index) to shoot the rope at, or go by the rope left on it; a hollow stone (cracks + its index). */
export const cliffOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.cliff;
  const w = s.senda?.cliff;
  if (!r || !w || at < 0 || w.done >= 0 || w.trip) return;
  if (at >= r.cracks.length + r.stones.length || at === w.hang) return;
  // (A hollow stone rings once: it cracks.)
  if (at >= r.cracks.length && w.rung[at - r.cracks.length]) return;
  w.job = at;
  w.shotAt = -1;
  h.target = 0;
  h.forced = false;
};

/** Another order of the hero's own: what it was sent to shoot is off. */
export const cliffOff = (s: BattleState): void => {
  const w = s.senda?.cliff;
  if (w && w.shotAt < 0) w.job = -1;
};

/** The way's length (sim units). */
const lenOf = (pts: CliffPoint[]): number => {
  let n = 0;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1] as CliffPoint;
    const b = pts[i] as CliffPoint;
    n += isqrt((b.x - a.x) * (b.x - a.x) + (b.y - a.y) * (b.y - a.y));
  }
  return n;
};

/** Where on its way someone is `d` along it. */
const along = (pts: CliffPoint[], d: number): { p: CliffPoint; done: boolean } => {
  let left = d;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1] as CliffPoint;
    const b = pts[i] as CliffPoint;
    const l = isqrt((b.x - a.x) * (b.x - a.x) + (b.y - a.y) * (b.y - a.y));
    if (left <= l && l > 0) return { p: { x: a.x + idiv((b.x - a.x) * left, l), y: a.y + idiv((b.y - a.y) * left, l) }, done: false };
    left -= l;
  }
  return { p: pts[pts.length - 1] as CliffPoint, done: true };
};

/** Crows out of a nest (or a flock from the crew's side). */
const crows = (s: BattleState, at: CliffPoint, n: number, spread = 600): void => {
  for (let k = 0; k < n; k++) {
    const e = spawnEnemy(s, 'crow', 1, false, 0, at.x + (k - idiv(n, 2)) * spread, at.y - 300 + (k % 2) * 500);
    if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
    e.anim = 'walk';
  }
};

/** Shriekers out of a deep crack. */
const shriekers = (s: BattleState, r: CliffRules, w: CliffState, at: CliffPoint, n: number): void => {
  const cap = r.night?.shriek.max ?? 9;
  for (let k = 0; k < n; k++) {
    if (w.shriekers.length >= cap) return;
    const e = spawnEnemy(s, 'crow', 1, false, 0, at.x + (k - 1) * 400, at.y + (k % 2) * 300);
    if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
    e.anim = 'walk';
    w.shriekers.push(e.id);
    w.bite.push(s.tick + 6);
  }
  ev(s, { t: 'senda', kind: 'shriekCall', npc: '', x: at.x, y: at.y, n });
};

/** A point pulled inside the board (a hex short of its edge). */
const onBoard = (s: BattleState, x: number, y: number): CliffPoint => {
  const lim = (s.map.radius - 2) * 866;
  const yy = y < -lim ? -lim : y > lim ? lim : y;
  const lx = (s.map.radius - 2) * 1000 - (Math.abs(yy) >> 1);
  return { x: x < -lx ? -lx : x > lx ? lx : x, y: yy };
};

/** The deep crack nearest a point. */
const nearestHole = (r: CliffRules, x: number, y: number): CliffPoint | null => {
  let hole: CliffPoint | null = null;
  let hd = 0x7fffffff;
  for (const p of r.holes) {
    const d = Math.abs(p.x - x) + Math.abs(p.y - y);
    if (d < hd) {
      hd = d;
      hole = p;
    }
  }
  return hole;
};

/** Something rang: the shriekers' last sound. */
const sound = (s: BattleState, w: CliffState, x: number, y: number): void => {
  w.sound = { x, y, t: s.tick };
};

/** Whether an enemy is one of this rule's own (the shriekers, their mother): their blows are theirs, not the common ones. */
export const cliffOwn = (s: BattleState, id: number): boolean => {
  const w = s.senda?.cliff;
  return !!w && (w.shriekers.includes(id) || (w.mother.id > 0 && w.mother.id === id));
};

/** Whether Sela is on a rope (the common walk leaves her to the trip). */
export const cliffHolds = (s: BattleState, n: SendaNpc): boolean => n.id === 'sela' && !!s.senda?.cliff?.sela;

const startTrip = (s: BattleState, r: CliffRules, w: CliffState, h: Hero, pts: CliffPoint[], end: number): void => {
  w.trip = { pts, t0: s.tick, end };
  w.on = -1;
  // The rope passing by a nest wakes it.
  for (let i = 0; i < r.nests.length; i++) {
    if (w.woke[i]) continue;
    const nst = r.nests[i] as CliffPoint;
    const L = lenOf(pts);
    for (let d = 0; d <= L; d += 500)
      if (near2(along(pts, d).p, nst.x, nst.y, r.nestR)) {
        w.woke[i] = 1;
        crows(s, nst, r.nestCrows);
        ev(s, { t: 'senda', kind: 'cliffNest', npc: '', x: nst.x, y: nst.y, n: i });
        break;
      }
  }
  // Sela's way after: from her ledge, the points the hero goes by.
  if (!w.trail.length) w.trail.push({ x: h.x, y: h.y });
  for (const p of pts.slice(1)) w.trail.push(p);
  ev(s, { t: 'senda', kind: 'cliffTrip', npc: '', x: h.x, y: h.y, n: end });
};

/** The shot at the crack leaves: what it is says how it sounds. */
const shot = (s: BattleState, r: CliffRules, w: CliffState, h: Hero, ci: number, cue: (c: string) => void): void => {
  const c = r.cracks[ci] as CliffRules['cracks'][number];
  w.sounded[ci] = 1;
  sound(s, w, c.x, c.y);
  // A second rope shot close by soon after brings them all (the night).
  if (r.night) {
    w.shots = w.shots.filter((t) => s.tick - t < 72);
    const close = w.shots.length;
    w.shots.push(s.tick);
    let hole: CliffPoint | null = null;
    let hd = 0x7fffffff;
    for (const p of r.holes) {
      const d = Math.abs(p.x - c.x) + Math.abs(p.y - c.y);
      if (d < hd) {
        hd = d;
        hole = p;
      }
    }
    if (hole) shriekers(s, r, w, hole, r.night.shriek.n + r.night.shriek.more * close);
    if (!w.shriekers.length) cue('shriek1');
  }
  if (c.kind === 1) {
    w.hollows++;
    ev(s, { t: 'senda', kind: 'cliffHollow', npc: '', x: c.x, y: c.y, n: ci });
    cue(`hollow${w.hollows}`);
    // The nest nearest a hollow crack takes fright.
    for (let i = 0; i < r.nests.length; i++) {
      const nst = r.nests[i] as CliffPoint;
      if (!w.woke[i] && near2(nst, c.x, c.y, r.nestR + 800)) {
        w.woke[i] = 1;
        crows(s, nst, r.nestCrows);
        ev(s, { t: 'senda', kind: 'cliffNest', npc: '', x: nst.x, y: nst.y, n: i });
        break;
      }
    }
    return;
  }
  if (c.kind === 0) {
    const firm = w.ropes.filter((x) => x).length + 1;
    ev(s, { t: 'senda', kind: 'cliffFirm', npc: '', x: c.x, y: c.y, n: ci });
    cue(`firm${firm}`);
    // Shot from under it (on the ledge it comes down on), the rope goes up to the ledge over it.
    const up = w.hang < 0 && w.on === c.land && c.top >= 0;
    const from = w.hang >= 0 ? (c.top >= 0 ? c.top : -1) : up ? c.top : w.on;
    if (from >= 0 && !w.ropes.some((x) => x.crack === ci)) w.ropes.push({ crack: ci, from });
    const foot = cliffFoot(s, r, ci);
    const top = up ? (spotInReach(s, r, c.top, c, r.rope, c) ?? (r.ledges[c.top] as CliffRules['ledges'][number]).mid) : foot;
    const pts = up ? [{ x: h.x, y: h.y }, { x: c.x, y: c.y }, top] : [{ x: h.x, y: h.y }, { x: c.x, y: c.y }, foot];
    w.hang = -1;
    startTrip(s, r, w, h, pts, up ? c.top : c.land);
    return;
  }
  // Crumbling: the hero swings to it and hangs.
  w.crumbs++;
  ev(s, { t: 'senda', kind: 'cliffCrumb', npc: '', x: c.x, y: c.y, n: ci });
  cue(`crumb${w.crumbs}`);
  if (w.hang < 0) w.from = w.on;
  w.hang = -1;
  startTrip(s, r, w, h, [{ x: h.x, y: h.y }, { x: c.x, y: c.y + 200 }], -1 - ci);
};

/** The hero going by rope: along its way, then on a ledge or hanging. */
const tripTick = (s: BattleState, r: CliffRules, w: CliffState, h: Hero, _cue: (c: string) => void): void => {
  const t = w.trip as CliffTrip;
  const speed = t.end === -1000 ? r.speed * 2 : r.speed;
  const at = along(t.pts, (s.tick - t.t0) * speed);
  h.x = h.ax = h.destX = at.p.x;
  h.y = h.ay = h.destY = at.p.y;
  h.path = [];
  h.moving = false;
  h.target = 0;
  const last = t.pts[t.pts.length - 1] as CliffPoint;
  h.facing = iatan2(last.y - h.y, last.x - h.x);
  if (!at.done) return;
  w.trip = null;
  if (t.end >= 0 || t.end === -1000) {
    const L = t.end === -1000 ? w.from : t.end;
    w.on = L;
    w.hang = -1;
    w.landed = s.tick;
    ev(s, { t: 'senda', kind: 'cliffLand', npc: '', x: h.x, y: h.y, n: L });
    // Sela comes down the same way (unless she is on this ledge already).
    const sela = s.senda?.npcs.find((n) => n.id === 'sela' && n.hp > 0);
    if (sela && cliffLedgeAt(s, r, sela.x, sela.y) !== L && w.trail.length > 1) {
      const pts = [{ x: sela.x, y: sela.y }, ...w.trail.slice(1), besideOn(s, r, L, h)];
      w.sela = { pts, t0: s.tick + 4, end: L };
    }
    w.trail = [];
    return;
  }
  w.hang = -1 - t.end;
  w.gives = s.tick + r.hold;
  // At night the swing onto it ends against the rock: a knock nothing on the wall spares (the way down goes by one).
  if (r.night?.knockBp) damageHero(s, h, mulBp(h.stats.maxHp, r.night.knockBp), null, 'physical', true);
  ev(s, { t: 'senda', kind: 'cliffHang', npc: '', x: h.x, y: h.y, n: w.hang });
};

/** Sela going by rope after the hero. */
const selaTick = (s: BattleState, w: CliffState, speed: number): void => {
  const t = w.sela as CliffTrip;
  const n = s.senda?.npcs.find((x) => x.id === 'sela');
  if (!n || n.hp <= 0) {
    w.sela = null;
    return;
  }
  if (s.tick < t.t0) return;
  const at = along(t.pts, (s.tick - t.t0) * speed);
  n.x = at.p.x;
  n.y = at.p.y;
  n.moving = true;
  if (at.done) w.sela = null;
};

/** The hero's job: walk to a spot in reach, aim, shoot. */
const jobTick = (s: BattleState, r: CliffRules, w: CliffState, h: Hero, cue: (c: string) => void): void => {
  if (w.job < 0 || h.stun > 0) return;
  const stone = w.job >= r.cracks.length ? r.stones[w.job - r.cracks.length] : undefined;
  const c = stone ?? (r.cracks[w.job] as CliffRules['cracks'][number]);
  // A rope left on it: go by it, from either end.
  if (!stone && w.hang < 0) {
    const rope = w.ropes.find((x) => x.crack === w.job);
    const cr = c as CliffRules['cracks'][number];
    if (rope && (w.on === rope.from || w.on === cr.land)) {
      const down = w.on === rope.from;
      const foot = cliffFoot(s, r, w.job);
      const top = r.ledges[rope.from];
      const up = top ? (spotInReach(s, r, rope.from, c, r.rope, c) ?? top.mid) : c;
      w.job = -1;
      startTrip(s, r, w, h, down ? [{ x: h.x, y: h.y }, { x: c.x, y: c.y }, foot] : [{ x: h.x, y: h.y }, foot, { x: c.x, y: c.y }, up], down ? cr.land : rope.from);
      return;
    }
  }
  const reach = stone ? imax(h.stats.range, 4000) : r.rope;
  if (w.shotAt < 0) {
    if (near2(h, c.x, c.y, reach)) {
      if (h.moving) {
        h.path = [];
        h.moving = false;
        h.destX = h.x;
        h.destY = h.y;
      }
      w.shotAt = s.tick + r.aim;
      h.facing = iatan2(c.y - h.y, c.x - h.x);
      h.attackCd = imax(h.attackCd, r.aim + 2);
      ev(s, { t: 'senda', kind: 'cliffShot', npc: '', x: h.x, y: h.y, n: w.job });
      return;
    }
    if (w.hang >= 0) {
      // Hanging: what isn't in reach from here isn't.
      ev(s, { t: 'senda', kind: 'cliffReach', npc: '', x: c.x, y: c.y, n: w.job });
      w.job = -1;
      return;
    }
    if (!h.moving) {
      const spot = w.on >= 0 ? spotInReach(s, r, w.on, c, reach, h) : null;
      if (!spot) {
        ev(s, { t: 'senda', kind: 'cliffReach', npc: '', x: c.x, y: c.y, n: w.job });
        w.job = -1;
        return;
      }
      orderMove(s, h, spot.x, spot.y);
      h.target = 0;
    }
    return;
  }
  h.facing = iatan2(c.y - h.y, c.x - h.x);
  if (s.tick < w.shotAt) return;
  const ci = w.job;
  w.job = -1;
  w.shotAt = -1;
  if (stone) {
    const si = ci - r.cracks.length;
    w.stone = { i: si, until: s.tick + (r.night?.stone ?? 48) };
    w.rung[si] = 1;
    sound(s, w, stone.x, stone.y);
    ev(s, { t: 'senda', kind: 'cliffStone', npc: '', x: stone.x, y: stone.y, n: si });
    cue('stone1');
    return;
  }
  shot(s, r, w, h, ci, cue);
};

/** The shriekers' and the mother's rule (`cliffHold`): to the last sound, biting who is there. */
export const cliffHold = (s: BattleState, e: Enemy): boolean => {
  const r = s.rules?.senda?.cliff;
  const w = s.senda?.cliff;
  if (!r || !w) return false;
  // Stunned (the ranger's net): the common rule counts it down (held here, it never would: frozen for good).
  if (e.stun > 0) return false;
  if (w.mother.id === e.id) return motherHold(s, r, w, e);
  const i = w.shriekers.indexOf(e.id);
  if (i < 0) return false;
  const ringing = w.stone.i >= 0 && s.tick < w.stone.until ? r.stones[w.stone.i] : undefined;
  const mem = r.night?.mem ?? 60;
  const t = ringing ?? (w.sound.t >= 0 && s.tick - w.sound.t <= mem ? w.sound : null);
  // Each its own place round the sound (they swarm, they don't stack).
  const ox = ((e.id * 137) % 1200) - 600;
  const oy = ((e.id * 251) % 800) - 400;
  if (t) {
    e.anim = 'walk';
    stepToward(e, t.x + ox, t.y + oy, r.shrieker.speed);
  } else {
    // No sound: they hang about where they are, circling.
    const k = (s.tick + e.id * 17) % 48;
    stepToward(e, e.x + (k < 24 ? 400 : -400), e.y + (k % 24 < 12 ? 300 : -300), idiv(r.shrieker.speed, 3));
  }
  // A bite at whoever is in reach (the hero; Sela, who is a common blow).
  if (s.tick >= (w.bite[i] ?? 0)) {
    const h = s.heroes.find((x) => x.downTicks === 0 && withinRange(x.x, x.y, e.x, e.y, r.shrieker.bite.reach));
    if (h) {
      w.bite[i] = s.tick + r.shrieker.bite.every;
      e.anim = 'attack';
      e.animT = 0;
      damageHero(s, h, mulBp(h.stats.maxHp, r.shrieker.bite.heroBp), e, 'physical', false);
    } else {
      const n = s.senda?.npcs.find((x) => x.id === 'sela' && x.hp > 0 && withinRange(x.x, x.y, e.x, e.y, r.shrieker.bite.reach));
      if (n) {
        w.bite[i] = s.tick + r.shrieker.bite.sela;
        e.anim = 'attack';
        e.animT = 0;
        n.hp = imax(0, n.hp - 1);
        ev(s, { t: 'senda', kind: 'hit', npc: n.id, x: n.x, y: n.y });
      }
    }
  }
  return true;
};

/** The mother: off the hero, over it on the wall; her shriek. */
const motherHold = (s: BattleState, r: CliffRules, w: CliffState, e: Enemy): boolean => {
  const M = r.night?.mother;
  if (!M) return true;
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h) return true;
  const m = w.mother;
  if (m.ring >= 0) {
    e.anim = 'cast';
    if (s.tick - m.ring >= M.warn) {
      m.ring = -1;
      m.shrieks++;
      ev(s, { t: 'senda', kind: 'shriek', npc: '', x: m.x, y: m.y, n: m.shrieks });
      for (const o of s.heroes)
        if (o.downTicks === 0 && withinRange(o.x, o.y, m.x, m.y, M.r)) {
          o.stun = imax(o.stun, M.stun);
          damageHero(s, o, mulBp(o.stats.maxHp, M.heroBp), e, 'physical', true);
        }
      for (const n of s.senda?.npcs ?? [])
        if (n.hp > 0 && withinRange(n.x, n.y, m.x, m.y, M.r)) {
          n.hp = imax(0, n.hp - 1);
          ev(s, { t: 'senda', kind: 'hit', npc: n.id, x: n.x, y: n.y });
        }
      m.next = s.tick + M.every;
    }
    return true;
  }
  // Up the wall from the hero, `keep` off, a little to its side.
  const side = (m.shrieks & 1) === 0 ? 1 : -1;
  const to = onBoard(s, h.x + side * idiv(M.keep, 2), h.y - M.keep);
  const tx = to.x;
  const ty = to.y;
  e.anim = 'walk';
  stepToward(e, tx, ty, 90);
  e.facing = iatan2(h.y - e.y, h.x - e.x);
  if (s.tick >= m.next) {
    m.ring = s.tick;
    m.x = e.x;
    m.y = e.y;
    ev(s, { t: 'senda', kind: 'shriekWarn', npc: '', x: e.x, y: e.y, n: M.warn });
  }
  return true;
};

export const cliffTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.cliff;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.cliff ??= fresh(r));
  if (!s.started || w.done >= 0) return;
  const h = s.heroes.find((x) => x.downTicks === 0);
  // The dead: out of the lists (ascending ids kept).
  if (w.shriekers.length) {
    const alive = new Set(s.enemies.filter((e) => e.hp > 0).map((e) => e.id));
    const keep: number[] = [];
    const bite: number[] = [];
    w.shriekers.forEach((id, i) => {
      if (alive.has(id)) {
        keep.push(id);
        bite.push(w.bite[i] ?? 0);
      }
    });
    w.shriekers = keep;
    w.bite = bite;
  }
  if (w.mother.id && !s.enemies.some((e) => e.id === w.mother.id && e.hp > 0)) w.mother.id = -1;
  if (w.sela) selaTick(s, w, r.speed + 80);
  if (!h) return;
  // (Never stranded: Sela off every ledge with no way under way comes back by the hero.)
  const sl = st.npcs.find((n) => n.id === 'sela' && n.hp > 0);
  if (sl && !w.sela && r.act !== 'edge' && w.on >= 0 && !w.trip && cliffLedgeAt(s, r, sl.x, sl.y) < 0) {
    const p = besideOn(s, r, w.on, h);
    sl.x = p.x;
    sl.y = p.y;
  }
  // The bow rang (the cornice, the night): a shot made its cooldown jump.
  if (h.attackCd > w.cd && r.act !== 'wall') {
    sound(s, w, h.x, h.y);
    // At night the bow calls one out of the nearest deep crack, too.
    if (r.night && s.tick >= w.bowCall) {
      w.bowCall = s.tick + r.night.shriek.bow;
      const hole = nearestHole(r, h.x, h.y);
      if (hole) shriekers(s, r, w, hole, 1);
    }
  }
  w.cd = h.attackCd;
  if (r.act === 'edge') {
    edgeTick(s, r, w, cue);
    return;
  }
  if (w.trip) tripTick(s, r, w, h, cue);
  else {
    // Hanging: still, the clock running; when it gives, the rope swings the hero back, struck.
    if (w.hang >= 0) {
      const c = r.cracks[w.hang] as CliffRules['cracks'][number];
      // The crack creaks under the weight: a sound, every second, where the hero hangs (at night it calls them).
      if (r.act === 'night' && (s.tick - w.gives) % 12 === 0) sound(s, w, c.x, c.y);
      h.x = h.ax = h.destX = c.x;
      h.y = h.ay = h.destY = c.y + 200;
      h.path = [];
      h.moving = false;
      if (s.tick >= w.gives && w.shotAt < 0) {
        w.falls++;
        const back = r.ledges[w.from];
        const to = back ? (spotInReach(s, r, w.from, c, r.rope + 3000, c) ?? back.mid) : { x: h.x, y: h.y + 2000 };
        ev(s, { t: 'senda', kind: 'cliffFall', npc: '', x: c.x, y: c.y, n: w.hang });
        cue(`fall${w.falls}`);
        damageHero(s, h, mulBp(h.stats.maxHp, r.fallBp), null, 'physical', true);
        w.hang = -1;
        w.job = -1;
        w.trip = { pts: [{ x: h.x, y: h.y }, to], t0: s.tick, end: -1000 };
        w.trail = [];
      }
    } else {
      const on = cliffLedgeAt(s, r, h.x, h.y);
      if (on >= 0) w.on = on;
    }
    if (!w.trip) jobTick(s, r, w, h, cue);
  }
  // The crew's scared flocks, from their side (the wall; at night too, in the dark: they peck at the hero, not Sela).
  if (s.waveT >= w.next.crew) {
    w.next.crew = s.waveT + r.crew.every;
    crows(s, { x: r.crewFrom.x, y: h.y - 1200 }, r.crew.n, 700);
    cue('crew');
  }
  if (r.act === 'night') nightTick(s, r, w, h, cue);
  const goal = r.ledges.findIndex((l) => l.goal);
  const sela = st.npcs.find((n) => n.id === 'sela');
  const selaIn = r.act === 'wall' || !sela || sela.hp <= 0 || (!w.sela && cliffLedgeAt(s, r, sela.x, sela.y) === goal);
  if (!w.trip && w.on === goal && w.hang < 0 && selaIn) {
    w.done = s.tick;
    ev(s, { t: 'senda', kind: 'cliffLand', npc: '', x: h.x, y: h.y, n: goal });
  }
};

/** The night: the mother comes; the voices of the crew. */
const nightTick = (s: BattleState, r: CliffRules, w: CliffState, h: Hero, cue: (c: string) => void): void => {
  const N = r.night;
  if (!N) return;
  if (!w.mother.id && s.waveT >= N.mother.at) {
    // (Over the hero, inside the board: out of it she would be lost.)
    const at = onBoard(s, h.x + 3000, h.y - 4000);
    const e = spawnEnemy(s, 'crow', 1, false, 0, at.x, at.y, N.mother.hp);
    e.anim = 'walk';
    w.mother.id = e.id;
    w.mother.next = s.tick + N.mother.first;
    w.mother.call = s.tick + N.mother.call;
    s.bossId = e.id;
    ev(s, { t: 'senda', kind: 'motherIn', npc: '', x: e.x, y: e.y });
    cue('mother');
  }
  // Her cry calls them out of the crack nearest the hero.
  if (w.mother.id > 0 && s.tick >= w.mother.call) {
    w.mother.call = s.tick + N.mother.call;
    let hole: CliffPoint | null = null;
    let hd = 0x7fffffff;
    for (const p of r.holes) {
      const d = Math.abs(p.x - h.x) + Math.abs(p.y - h.y);
      if (d < hd) {
        hd = d;
        hole = p;
      }
    }
    if (hole) shriekers(s, r, w, hole, 2);
  }
  if (s.waveT >= 480) cue('voices');
};

/** The cornice: the hammer, the nests, the deep cracks; the clock. */
const edgeTick = (s: BattleState, r: CliffRules, w: CliffState, cue: (c: string) => void): void => {
  const E = r.edge;
  if (!E) return;
  const sela = s.senda?.npcs.find((n) => n.id === 'sela' && n.hp > 0);
  if (sela && s.waveT >= w.next.hammer) {
    w.next.hammer = s.waveT + E.hammer;
    sound(s, w, sela.x, sela.y);
    ev(s, { t: 'senda', kind: 'cliffHammer', npc: 'sela', x: sela.x, y: sela.y });
  }
  if (s.waveT >= w.next.crow) {
    w.next.crow = s.waveT + E.crows.every;
    crows(s, r.nests[rngInt(s.rng, r.nests.length)] as CliffPoint, E.crows.n);
  }
  if (s.waveT >= w.next.shriek) {
    const first = w.next.shriek === E.shriek.first;
    w.next.shriek = s.waveT + E.shriek.every;
    shriekers(s, r, w, r.holes[rngInt(s.rng, r.holes.length)] as CliffPoint, E.shriek.n);
    if (first) cue('shriek1');
  }
  if (s.waveT >= 72) cue('crew');
  if (s.waveT >= idiv(E.time, 2)) cue('half');
  if (sela && sela.hp < sela.maxHp && !w.hollows) {
    w.hollows = 1;
    cue('hurt');
  }
  if (s.waveT >= E.time) w.done = s.tick;
};

export const cliffGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.cliff;
  const w = s.senda?.cliff;
  if (r?.act === 'edge' && r.edge) return { done: w && w.done >= 0 ? r.edge.time : imax(0, s.waveT), need: r.edge.time };
  if (!r || !w) return { done: 0, need: 1 };
  if (w.done >= 0) return { done: 1, need: 1 };
  return { done: 0, need: 1 };
};

export const cliffResult = (s: BattleState): 'won' | 'lost' | null => {
  const w = s.senda?.cliff;
  if (!w || w.done < 0) return null;
  return s.tick >= w.done + 18 ? 'won' : null;
};
